<script lang="ts">
export default {
  name: 'Prices'
}
</script>

<script setup lang="ts">
import {ref, computed, PropType, nextTick} from "vue";
import {useGlobalStore} from "../../../stores/global";

const props = defineProps({
  header: { type: Boolean, required: false, default: false },
  extraClass: { type: String, required: false },
  package: { type: Object as PropType<{price: number, name: string}>, required: true },
  trans: { type: Object, required: true },
  my_registration: { type: Object, required: true },
  prices: { type: Object, required: true },
  toast: { type: Function, required: true },
  saveUrl: { type: String, required: true },
  saveVoucher: { type: Function, required: true },
});

const store = useGlobalStore();

const subTotal = computed((): number => {
  let ret: number = props.package.price;

  if (props.my_registration.extraOption.ee.eePackage) {
    ret += props.prices.options.eepackage;
  } else {
    (props.my_registration.extraOption.ee.newsletter) && (ret += props.prices.options.newsletter);
    (props.my_registration.extraOption.ee.moveUp) && (ret += props.prices.options.moveup);
    (props.my_registration.extraOption.ee.featuredCompany) && (ret += props.prices.options.featured);
    (props.my_registration.extraOption.ee.homepage) && (ret += props.prices.options.homepage);
  }

  (props.my_registration.extraOption.ee.facebook) && (ret += props.prices.options.facebook);
  (props.my_registration.extraOption.ee.linkedin) && (ret += props.prices.options.linkedin);

  (props.my_registration.extraOption.as.responseManager) && (ret += props.prices.options.response);
  (props.my_registration.extraOption.as.directMailing) && (ret += props.prices.options.mailing);
  (props.my_registration.extraOption.as.mailingShortList) && (ret += props.prices.options.shortlist);

  if (props.my_registration.extraOption.ip.ipPackage) {
    ret += props.prices.options.package;
  } else {
    (store.webspace !== 'btknl') && props.my_registration.extraOption.ip.netherlands && (ret += props.prices.options.netherlands);
    (store.webspace !== 'btkbe') && props.my_registration.extraOption.ip.belgium && (ret += props.prices.options.belgium);
    (store.webspace !== 'btkde') && props.my_registration.extraOption.ip.germany && (ret += props.prices.options.germany);
    (store.webspace !== 'btkes') && props.my_registration.extraOption.ip.spain && (ret += props.prices.options.spain);
  }

  return ret;
});

const totalExcl = computed((): number => {
  let ret = subTotal.value;

  if (props.my_registration.searchservice_discount) {
    ret -= props.my_registration.searchservice_discount;
  }
  if (props.my_registration.voucher_discount) {
    ret -= props.my_registration.voucher_discount;
  }

  return ret;
});

const isVatNull = computed(() => {
  return (props.my_registration.personalData.foreignCompany && props.my_registration.personalData.isVatNull)
});
const vat = computed(() => {
  if (isVatNull.value) {
    return 0;
  }

  return totalExcl.value / 100 * props.prices.vat;
});
const totalIncl = computed(() => {
  return totalExcl.value + vat.value;
});

const numberFormat = function(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);//.replace(/,00$/, ',-');
}

function showVoucherInput() {
  props.my_registration.enter_voucher = true;
  nextTick(() => {
    document.getElementById('reaction-form-voucher')?.focus();
  });
}
function hideVoucherInput() {
  props.my_registration.enter_voucher = false;
  props.my_registration.voucher = '';
  props.my_registration.voucher_discount = null;
}

const saving = ref(false);
async function checkVoucher() {
  if (saving.value) {
    return;
  }
  if (!props.my_registration.voucher) {
    props.my_registration.voucher_discount = 0;

    return;
  }

  saving.value = true;
  const res = await fetch(props.saveUrl, {
    method: 'POST',
    body: JSON.stringify({
      user: store.user,
      registration: props.my_registration,
    }),
    headers: {
      'content-type': 'application/json',
      'x-registration-token': props.my_registration.token || '',

      'x-save-type': 'check-voucher',
      'x-voucher-code': props.my_registration.voucher,
      'x-package': props.my_registration.purpose.toString(),
    },
  });
  saving.value = false;

  if (!res.ok) {
    props.toast(props.trans.toast_error_general);
  }
  const data = await res.json();
  if (data.success) {
    props.my_registration.voucher_discount = data.voucher_discount;
    props.my_registration.enter_voucher = false;
    props.saveVoucher();
  } else {
    props.toast(props.trans.step6.voucher_error);
    props.my_registration.voucher_discount = 0;
  }
}
</script>

<template>
  <table class="checkout-table checkout-table-prices" :class="extraClass">
    <thead v-if="header">
      <tr>
        <th>{{ trans.step6.service }}</th>
        <th class="value">{{ trans.step6.price }}</th>
      </tr>
    </thead>
    <tbody>
    <tr class="option">
      <td>{{ package.name }}</td>
      <td class="value">&euro; {{ numberFormat(package.price) }}</td>
    </tr>

    <tr v-if="my_registration.searchservice_discount" class="option">
      <td>{{ trans.step4.searchservice }}</td>
      <td class="value">&euro; {{ numberFormat(my_registration.searchservice_discount * -1) }}</td>
    </tr>

    <template v-if="my_registration.extraOption.ee.eePackage">
      <tr class="option">
        <td>{{ trans.step4.eepackage }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.eepackage) }}</td>
      </tr>
    </template>
    <template v-else>
      <tr class="option" v-if="my_registration.extraOption.ee.newsletter">
        <td>{{ trans.step4.newsletter }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.newsletter) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ee.moveUp">
        <td>{{ trans.step4.moveup }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.moveup) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ee.featuredCompany">
        <td>{{ trans.step4.featured }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.featured) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ee.homepage">
        <td>{{ trans.step4.homepage }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.homepage) }}</td>
      </tr>
    </template>

    <tr class="option" v-if="my_registration.extraOption.ee.facebook">
      <td>{{ trans.step4.facebook }}</td>
      <td class="value">&euro; {{ numberFormat(prices.options.facebook) }}</td>
    </tr>
    <tr class="option" v-if="my_registration.extraOption.ee.linkedin">
      <td>{{ trans.step4.linkedin }}</td>
      <td class="value">&euro; {{ numberFormat(prices.options.linkedin) }}</td>
    </tr>

    <tr class="option" v-if="my_registration.extraOption.as.responseManager">
      <td>{{ trans.step4.response }}</td>
      <td class="value">&euro; {{ numberFormat(prices.options.response) }}</td>
    </tr>
    <tr class="option" v-if="my_registration.extraOption.as.directMailing">
      <td>{{ trans.step4.mailing }}</td>
      <td class="value">&euro; {{ numberFormat(prices.options.mailing) }}</td>
    </tr>
    <tr class="option" v-if="my_registration.extraOption.as.mailingShortList">
      <td>{{ trans.step4.shortlist }}</td>
      <td class="value">&euro; {{ numberFormat(prices.options.shortlist) }}</td>
    </tr>

    <template v-if="my_registration.extraOption.ip.ipPackage">
      <tr class="option">
        <td>{{ trans.step4.package }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.package) }}</td>
      </tr>
    </template>
    <template v-else>
      <tr class="option" v-if="my_registration.extraOption.ip.netherlands">
        <td>{{ trans.step4.netherlands }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.netherlands) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ip.belgium">
        <td>{{ trans.step4.belgium }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.belgium) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ip.germany">
        <td>{{ trans.step4.germany }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.germany) }}</td>
      </tr>
      <tr class="option" v-if="my_registration.extraOption.ip.spain">
        <td>{{ trans.step4.spain }}</td>
        <td class="value">&euro; {{ numberFormat(prices.options.spain) }}</td>
      </tr>
    </template>

    <template v-if="header">
      <tr v-if="!my_registration.voucher_discount" class="option">
        <template v-if="!my_registration.enter_voucher">
          <td colspan="2" style="padding-top: 5px;">
            <button v-if="!my_registration.voucher_discount" class="btn-hollow btn-xs" @click.prevent="showVoucherInput">&plus; {{ trans.step5.voucher }}</button>
          </td>
        </template>
        <template v-else>
          <td colspan="2">
            <label for="reaction-form-voucher">{{ trans.step5.voucher }}</label>
            <div class="voucher-wrapper">
              <button @click.prevent="hideVoucherInput" class="ico-xmark-solid"></button>
              <input v-model="my_registration.voucher" id="reaction-form-voucher" type="text"
                     @keydown.enter.prevent="checkVoucher"
                     @keydown.esc.prevent="hideVoucherInput"
              />
              <button @click.prevent="checkVoucher" :disabled="saving" :class="saving ? 'icon-spinner' : 'ico-check-sharp-solid'"></button>
            </div>
          </td>
        </template>
      </tr>
      <tr v-else class="option">
        <td>
          {{ trans.step5.voucher }} {{my_registration.voucher}}
          <button @click.prevent="my_registration.voucher_discount = 0; my_registration.voucher = '';" class="btn-solid btn-xs">&times;</button>
        </td>
        <td class="value">&euro; {{ numberFormat(my_registration.voucher_discount * -1) }}</td>
      </tr>
    </template>
    <template v-else-if="my_registration.voucher_discount">
      <tr class="option">
        <td>
          {{ trans.step5.voucher }} {{my_registration.voucher}}
        </td>
        <td class="value">&euro; {{ numberFormat(my_registration.voucher_discount * -1) }}</td>
      </tr>
    </template>

    <tr class="total">
      <td>{{ trans.total_excl }}</td>
      <td class="value">&euro; {{ numberFormat(totalExcl) }}</td>
    </tr>
    <tr>
      <td>{{ trans.vat }} ({{ isVatNull ? '0' : prices.vat }}%)</td>
      <td class="value">&euro; {{ numberFormat(vat) }}</td>
    </tr>
    <tr>
      <td>{{ trans.total_incl }}</td>
      <td class="value">&euro; {{ numberFormat(totalIncl) }}</td>
    </tr>
    </tbody>
  </table>
</template>